import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  Checkbox,
  Spinner,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCreateUser } from "../utils/auth.api";
import { useNavigate } from "react-router-dom";
// import {useCreateUser} from ".."
// import { useCreateUser } from "../utils/auth.api";
import Header from '../Components/Header';
import { setTokenWithExpiry } from '../utils/tokenManager';

export default function SignUp() {
  const createAccountMutation = useCreateUser();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  // Initialize fromVehicleForm from localStorage on mount
  const [fromVehicleForm, setFromVehicleForm] = useState(() => {
    return localStorage.getItem('fromVehicleForm') === 'true';
  });
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    hasCapital: false,
    hasNumber: false,
    hasSpecial: false
  });

  // Generate random password that meets requirements
  const generateRandomPassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    // Ensure at least one of each required character
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest to make it at least 8 characters
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Extract username from email (part before @)
  const extractUsernameFromEmail = (email) => {
    if (!email) return '';
    const parts = email.split('@');
    return parts[0] || '';
  };

  // Password validation functions
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      hasCapital: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    return validation;
  };

  const isPasswordValidCheck = (validation) => {
    return validation.length && validation.hasCapital && validation.hasNumber && validation.hasSpecial;
  };

  // Check for email from localStorage (set by VehicleDetailsForm)
  useEffect(() => {
    const storedEmail = localStorage.getItem('signupEmail');
    const fromVehicle = localStorage.getItem('fromVehicleForm') === 'true';
    
    // Set the flag first, regardless of whether email exists
    if (fromVehicle) {
      setFromVehicleForm(true);
    }
    
    if (storedEmail) {
      setEmail(storedEmail);
      
      // If coming from vehicle form, auto-generate password and username
      if (fromVehicle) {
        const generatedPassword = generateRandomPassword();
        const extractedUsername = extractUsernameFromEmail(storedEmail);
        setPassword(generatedPassword);
        setUserName(extractedUsername);
        // Validate the generated password
        const validation = validatePassword(generatedPassword);
        setPasswordValidation(validation);
        setIsPasswordValid(isPasswordValidCheck(validation));
      }
      
      // Clear the stored email after using it
      localStorage.removeItem('signupEmail');
    }
    
    // Clear the flag after a small delay to ensure state has updated
    if (fromVehicle) {
      setTimeout(() => {
        localStorage.removeItem('fromVehicleForm');
      }, 100);
    }
  }, []);

  // Handle password change with real-time validation
  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setPassword(password);
    
    const validation = validatePassword(password);
    setPasswordValidation(validation);
    setIsPasswordValid(isPasswordValidCheck(validation));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // For vehicle form flow, username and password are auto-generated, so skip validation
    if (!fromVehicleForm) {
      if (!username || !email || !password) {
        alert("Please fill all fields");
        return;
      }
    } else {
      // Ensure email is filled even in vehicle form flow
      if (!email) {
        alert("Email is required");
        return;
      }
    }
  
    if (!agreeToTerms) {
      alert("Please agree to Terms of Service and Privacy Policy");
      return;
    }
  
    // Use current values, or fallback to auto-generated ones
    // For vehicle form flow, these should already be set in useEffect, but use fallback just in case
    const finalUsername = username || extractUsernameFromEmail(email);
    const finalPassword = password || generateRandomPassword();
  
    try {
      const response = await createAccountMutation.mutateAsync({
        email: email,
        username: finalUsername,
        firstname: "firstname",
        lastname: "lastname",
        password: finalPassword,
      });
  
      console.log("response of sign up:", response);
  
      if (response?.access_token) {
        // ✅ Store login response in localStorage
        setTokenWithExpiry(response.access_token);
        localStorage.setItem('username', response.username);
        localStorage.setItem('userid', response.userid);
        localStorage.setItem('email', response.email);
        
        // ✅ Clear form fields
        setUserName("");
        setEmail("");
        setPassword("");
        setAgreeToTerms(false);
  
        // ✅ Show success message from API response or default message
        const successMessage = response?.message || 'Account created successfully';
        toast.success(successMessage);
  
        // ✅ Navigate to dashboard after successful registration
        navigate('/dashboard');
      } else {
        // ❌ If access_token not present
        toast.error("Failed to create account. Please try again.");
      }
    } catch (error) {
      console.error("❌ Account creation failed:", error.message);
      toast.error(error.message || "Account creation failed. Please try again.");
    }
  };
  

  return (
    <Box minH="100vh" bg="white">
      <Header hasToken={!!localStorage.getItem('token')} onMenuClick={() => {}} />
      <Container maxW="sm" centerContent>
        <VStack spacing={8} w="100%" pt={12}>
          {/* Header Section */}
          <Box textAlign="left" w="100%">
            <Heading as="h1" size="lg" color="gray.900" fontWeight="bold" lineHeight="1.2">
              Create your new
            </Heading>
            <Heading as="h1" size="lg" color="gray.900" fontWeight="bold" lineHeight="1.2">
              account
            </Heading>
          </Box>
        

          {/* Form Section */}
          <VStack as="form" spacing={6} w="100%" onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel color="gray.700" fontWeight="medium">Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                size="lg"
                bg="gray.50"
                borderColor="gray.200"
                borderRadius="lg"
                _placeholder={{ color: "gray.600" }}
                isDisabled={createAccountMutation.isLoading || fromVehicleForm}
              />
            </FormControl>

            {!fromVehicleForm && (
              <FormControl>
                <FormLabel color="gray.700" fontWeight="medium">User Name</FormLabel>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder=""
                  size="lg"
                  bg="gray.50"
                  borderColor="gray.200"
                  borderRadius="lg"
                  _placeholder={{ color: "gray.600" }}
                  isDisabled={createAccountMutation.isLoading}
                />
              </FormControl>
            )}

            {!fromVehicleForm && (
              <FormControl>
                <FormLabel color="gray.700" fontWeight="medium">Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder=""
                    size="lg"
                    bg="gray.50"
                    borderColor={
                      password === '' 
                        ? 'gray.200' 
                        : isPasswordValid 
                          ? 'green.400' 
                          : 'red.400'
                    }
                    borderWidth={password === '' ? '1px' : '2px'}
                    borderRadius="lg"
                    _placeholder={{ color: 'gray.600' }}
                    _focus={{
                      borderColor: isPasswordValid ? 'green.400' : 'red.400',
                      borderWidth: '2px',
                      boxShadow: isPasswordValid 
                        ? '0 0 0 1px rgba(72, 187, 120, 0.3)' 
                        : '0 0 0 1px rgba(245, 101, 101, 0.3)'
                    }}
                    isDisabled={createAccountMutation.isLoading}
                  />
                  <InputRightElement w="3rem" h="100%">
                    <IconButton
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <ViewIcon fontSize="lg" /> :<ViewOffIcon fontSize="lg" /> }
                      size="sm"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      minW="auto"
                      h="auto"
                    />
                  </InputRightElement>
                </InputGroup>
                <VStack spacing={1} align="start" mt={2}>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Password must contain:
                  </Text>
                  <VStack spacing={0.5} align="start" ml={2}>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.length ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.length ? '✓' : '✗'} At least 8 characters
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasCapital ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasCapital ? '✓' : '✗'} At least One capital letter
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasNumber ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasNumber ? '✓' : '✗'} At least One number
                    </Text>
                    <Text 
                      fontSize="xs" 
                      color={passwordValidation.hasSpecial ? 'green.500' : 'red.500'}
                      display="flex"
                      alignItems="center"
                    >
                      {passwordValidation.hasSpecial ? '✓' : '✗'} At least One special character
                    </Text>
                  </VStack>
                </VStack>
              </FormControl>
            )}

            {/* Terms Checkbox */}
            <Box w="100%" textAlign="left">
              <Checkbox
                isChecked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                colorScheme="red"
                size="lg"
                iconColor="white"
              >
                <Text fontSize="sm" color="gray.700">
                  I Agree with{' '}
                  <Text as="span" color="red.500" fontWeight="medium">
                    Terms of Service
                  </Text>
                  {' '}and{' '}
                  <Text as="span" color="red.500" fontWeight="medium">
                    Privacy Policy
                  </Text>
                </Text>
              </Checkbox>
            </Box>

            {/* Register Button */}
            <Button
              type="submit"
              w="100%"
              bg="#5A5BED"
              height="50px"
              color="white"
              fontWeight="semibold"
              borderRadius="100px"
              isLoading={createAccountMutation.isLoading}
              loadingText="Creating..."
              spinner={<Spinner size="sm" color="white" />}
              isDisabled={!agreeToTerms}
              _hover={{ bg: "#4A4BDD" }}
              _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            >
              Register
            </Button>
          </VStack>

          {/* Desktop: show in normal flow */}
          <Box
            display={{ base: "none", md: "block" }}
            pt={4}
            w="100%"
          >
            <Text color="gray.600" textAlign="center">
              Already have an account?{' '}
              <span 
                style={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate('/sign-in')}
              >
                Sign In
              </span>
            </Text>
          </Box>

          {/* Mobile: fix to bottom */}
          <Box
            display={{ base: "block", md: "none" }}
            position="fixed"
            bottom="0"
            left="0"
            w="100vw"
            bg="white"
            py={4}
            boxShadow="0 -2px 8px rgba(0,0,0,0.03)"
            zIndex={10}
          >
            <Text color="gray.600" textAlign="center">
              Already have an account?{' '}
              <span 
                style={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate('/sign-in')}
              >
                Sign In
              </span>
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}